import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import { X, ArrowLeft } from 'react-feather';
import { Multiselect } from 'multiselect-react-dropdown';
import {
    addSale,
    getClientOptions
} from '../../../actions/sale';
import { getSparepartOptions, getWarehouseOptions } from '../../../actions/spare_part';
import { getSelectedOption } from '../../../utils/hooks/getParams';
import '../material-sales/MaterialSaleCreateForm.css';
import useActiveFirm from '../../../hooks/useActiveFirm';

const SparepartSaleCreateForm = ({ onClose }) => {
    const dispatch = useDispatch();
    const { firmId, firmName } = useActiveFirm();
    const { sale, user, spare_part } = useSelector(state => state);
    const {
        client_options,
        error,
        loading,
        show
    } = sale;
    const { warehouse_options, spare_part_options } = spare_part;

    const defaultValues = {
        is_special_sale: false,
        firm_id: '',
        client_id: '',
        warehouse_id: '',
        date: moment().format('YYYY-MM-DD'),
        transport_number: '',
        driver_name: '',
        description: '',
        items: []
    };

    const [inputs, setInputs] = useState(defaultValues);
    const [errors, setErrors] = useState({});
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [sparepartOptions, setSparepartOptions] = useState(spare_part_options || []);
    const [selectedDynamicSparepartOptions, setSelectedDynamicSparepartOptions] = useState([]);
    const [selectedSparepartOptions, setSelectedSparepartOptions] = useState([]);
    const [clientOptions, setClientOptions] = useState([]);

    const handleError = (errors) => {
        setErrors(errors);
    };

    const clearErrors = () => {
        setErrors({});
    };

    const formRef = useRef();

    useEffect(() => {
        dispatch(getClientOptions());
    }, []);

    // Auto-populate with global firm when form is shown
    useEffect(() => {
        if (show && firmId) {
            setInputs(prev => ({ ...prev, firm_id: firmId }));
            dispatch(getWarehouseOptions({ firm_id: firmId, type: 'part' }));
            dispatch(getSparepartOptions({ is_for_sale: true, firm_id: firmId }));
        }
    }, [show, firmId, dispatch]);

    useEffect(() => {
        if (show) {
            dispatch(getSparepartOptions({ is_for_sale: true }));
        }
    }, [show]);

    useEffect(() => {
        clearErrors();
    }, [sale]);


    useEffect(() => {
        let options = [];
        warehouse_options?.forEach((item) => options.push({
            label: item.name,
            value: item.id
        }));
        setWarehouseOptions(options);
    }, [warehouse_options]);

    const handleCloseForm = () => {
        onClose();
    };

    const resetForm = () => {
        setInputs(defaultValues);
        setErrors({});
        setSparepartOptions([]);
        setSelectedSparepartOptions([]);
        setSelectedDynamicSparepartOptions([]);
    };

    useEffect(() => {
        if (spare_part_options && spare_part_options.length > 0) {
            const items = spare_part_options.map((item) => ({
                id: item.id,
                name: item.name,
                quantity: 0,
                price: 0,
                net_price: item?.price,
                in_stock: item?.in_stock,
                unit: item?.unit,
            }));
            setSparepartOptions(items);
        }
    }, [spare_part_options]);

    useEffect(() => {
        let options = [];
        client_options?.forEach((item) => options.push({
            label: item.name,
            value: item.id
        }));
        setClientOptions(options);
    }, [client_options]);

    const onSelectSparepart = (data) => {
        setSelectedDynamicSparepartOptions(data);
    };

    const onRemoveSparepart = (data) => {
        setSelectedDynamicSparepartOptions(data);
    };

    const onClickMoveSelectedSpareparts = () => {
        if (selectedDynamicSparepartOptions.length) {
            const inputSpareparts = selectedDynamicSparepartOptions.map(item => ({
                id: item.id,
                name: item.name,
                quantity: 0,
                price: 0,
                net_price: item?.net_price,
                in_stock: item?.in_stock,
                unit: item?.unit,
            }));

            // Update selected spareparts
            setSelectedSparepartOptions([...selectedSparepartOptions, ...inputSpareparts]);
            
            // Remove selected items from available options
            const selectedIds = inputSpareparts.map(item => item.id);
            const filteredSpareparts = sparepartOptions.filter(item => !selectedIds.includes(item.id));
            setSparepartOptions(filteredSpareparts);
            
            // Clear dynamic selection
            setSelectedDynamicSparepartOptions([]);
            
            // Update form inputs
            setInputs(state => ({ ...state, items: [...state.items, ...inputSpareparts] }));
        }
    };

    const onRemoveSelectedSparepart = (id) => {
        // Remove from selected spareparts
        const updatedSelectedSpareparts = selectedSparepartOptions.filter(item => item.id !== id);
        setSelectedSparepartOptions(updatedSelectedSpareparts);

        // Add back to available options
        const removedSparepart = selectedSparepartOptions.find(item => item.id === id);
        if (removedSparepart) {
            setSparepartOptions([...sparepartOptions, removedSparepart]);
        }

        // Update form inputs
        const cleanItems = updatedSelectedSpareparts.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item?.quantity || 0,
            price: item?.price || 0,
            net_price: item?.net_price,
            in_stock: item?.in_stock,
            unit: item?.unit,
        }));
        setInputs(state => ({ ...state, items: cleanItems }));
    };

    const handleSelect = (data, input) => {
        clearErrors();
        setInputs(state => ({
            ...state,
            [input.name]: data?.value || ''
        }));
    };

    const handleChange = (event) => {
        clearErrors();
        setInputs(state => ({
            ...state,
            [event.target.name]: event.target.value
        }));
    };

    const onChangeInput = (e) => {
        const { name, value } = e.target;
        const itemId = parseInt(e.target.dataset.id);
        
        const updatedItems = selectedSparepartOptions.map(item => {
            if (item.id === itemId) {
                return { ...item, [name]: parseFloat(value) || 0 };
            }
            return item;
        });
        
        setSelectedSparepartOptions(updatedItems);
        
        const cleanItems = updatedItems.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item?.quantity || 0,
            price: item?.price || 0,
            net_price: item?.net_price,
            in_stock: item?.in_stock,
            unit: item?.unit,
        }));
        setInputs(state => ({ ...state, items: cleanItems }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationErrors = {};

        if (!inputs.firm_id) {
            validationErrors.firm_id = ["Firma tanlash majburiy"];
        }

        if (!inputs.client_id) {
            validationErrors.client_id = ["Mijoz tanlash majburiy"];
        }

        if (!inputs.warehouse_id) {
            validationErrors.warehouse_id = ["Ombor tanlash majburiy"];
        }

        if (!inputs.transport_number) {
            validationErrors.transport_number = ["Transport raqami majburiy"];
        }

        if (!inputs.driver_name) {
            validationErrors.driver_name = ["Haydovchi ismi majburiy"];
        }

        if (!inputs.items.length) {
            validationErrors.items = ["Ehtiyot qismlar tanlash majburiy"];
        } else {
            // Check sparepart quantities
            const invalidQuantities = inputs.items.filter(item => {
                const sparepart = sparepartOptions.find(s => s.id === item.id);
                return !item.quantity || item.quantity <= 0 || (sparepart && item.quantity > sparepart.in_stock);
            });

            if (invalidQuantities.length > 0) {
                validationErrors['items.0.quantity'] = ["Miqdor ombordagi qoldiqdan ko'p bo'lmasligi kerak"];
            }
        }

        if (Object.keys(validationErrors).length > 0) {
            handleError(validationErrors);
            return;
        }

        const data = {
            client_id: parseInt(inputs.client_id),
            firm_id: parseInt(inputs.firm_id),
            warehouse_id: parseInt(inputs.warehouse_id),
            date: inputs.date,
            transport_number: inputs.transport_number,
            driver_name: inputs.driver_name,
            description: inputs.description,
            comment: inputs.description,
            items: inputs.items.map(item => ({
                sellable_type: 'App\\Models\\SparePart',
                sellable_id: parseInt(item.id),
                quantity: parseFloat(item.quantity),
                price: parseFloat(item.price)
            }))
        };

        try {
            const response = await dispatch(addSale(data));
            if (response) {
                resetForm();
                handleCloseForm();
            }
        } catch (err) {
            if (err.errors) {
                handleError(err.errors);
                if (err.errors['items.0.quantity']) {
                    const sparepartErrors = { ...errors };
                    sparepartErrors.quantity = err.errors['items.0.quantity'][0];
                    setErrors(sparepartErrors);
                }
            }
        }
    };

    return (
        <form onSubmit={handleSubmit} ref={formRef}>
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Firma</label>
                        <input
                            name="firm_id"
                            className="form-control"
                            type="text"
                            value={firmName || ''}
                            readOnly
                            disabled
                            style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                        />
                        <small className="text-muted">Firma global firm switcher orqali tanlangan</small>
                        {errors.firm_id && <div className="invalid-feedback">{errors.firm_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Ombor</label>
                        <Select
                            value={getSelectedOption(inputs.warehouse_id, warehouseOptions)}
                            name="warehouse_id"
                            onChange={(data) => handleSelect(data, {name: 'warehouse_id'})}
                            options={warehouseOptions}
                            placeholder="Omborni tanlang"
                            className={`react-select-container ${errors.warehouse_id ? 'is-invalid' : ''}`}
                        />
                        {errors.warehouse_id && <div className="invalid-feedback">{errors.warehouse_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label>Mijoz</label>
                        <Select
                            value={getSelectedOption(inputs.client_id, clientOptions)}
                            name="client_id"
                            onChange={(data) => handleSelect(data, {name: 'client_id'})}
                            options={clientOptions}
                            placeholder="Mijozni tanlang"
                            className={`react-select-container ${errors.client_id ? 'is-invalid' : ''}`}
                        />
                        {errors.client_id && <div className="invalid-feedback">{errors.client_id}</div>}
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group">
                        <label htmlFor="date">Sana</label>
                        <input
                            type="date"
                            name="date"
                            className={`form-control ${errors.date ? 'is-invalid' : ''}`}
                            max={moment().format('YYYY-MM-DD')}
                            value={inputs?.date || ''}
                            onChange={handleChange}
                        />
                        {errors.date && <div className="invalid-feedback">{errors.date}</div>}
                    </div>
                </div>
            </div>

            <div className="mt-4">
                <div className="mb-3">
                    <div className="row g-3">
                        <div className="col-md-12">
                            <div className="form-group">
                                <Multiselect
                                    options={sparepartOptions}
                                    selectedValues={selectedDynamicSparepartOptions}
                                    onSelect={onSelectSparepart}
                                    onRemove={onRemoveSparepart}
                                    displayValue="name"
                                    placeholder="Ehtiyot qismlarni tanlang"
                                />
                            </div>
                        </div>
                        <div className="col-md-12">
                            <div className="d-flex justify-content-end">
                                <Button
                                    variant="primary"
                                    onClick={onClickMoveSelectedSpareparts}
                                    disabled={selectedDynamicSparepartOptions.length === 0}
                                >
                                    <ArrowLeft size={18} /> Qo'shish
                                </Button>
                            </div>
                        </div>
                    </div>

                    {selectedSparepartOptions.length > 0 && (
                        <div className="mt-4">
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover sales_table">
                                    <thead>
                                        <tr>
                                            <th>Ehtiyot qism nomi</th>
                                            <th>Qoldiq</th>
                                            <th>Tan narxi</th>
                                            <th>Miqdori</th>
                                            <th>Narxi</th>
                                            <th>Jami</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedSparepartOptions.map((sparepart) => (
                                            <tr key={sparepart.id}>
                                                <td>{sparepart.name}</td>
                                                <td>{sparepart.in_stock} {sparepart.unit?.symbol || 'dona'}</td>
                                                <td>{sparepart.net_price}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`form-control form-control-sm ${errors.quantity ? 'is-invalid' : ''}`}
                                                        name="quantity"
                                                        data-id={sparepart.id}
                                                        value={sparepart?.quantity || ''}
                                                        onChange={onChangeInput}
                                                    />
                                                    {errors.quantity && (
                                                        <div className="invalid-feedback">
                                                            {errors.quantity}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control form-control-sm"
                                                        name="price"
                                                        data-id={sparepart.id}
                                                        value={sparepart?.price || ''}
                                                        onChange={onChangeInput}
                                                        min="0"
                                                    />
                                                </td>
                                                <td>
                                                    {sparepart?.quantity && sparepart?.price
                                                        ? Math.floor(sparepart.quantity * sparepart.price).toLocaleString('uz-UZ')
                                                        : '0'}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => onRemoveSelectedSparepart(sparepart.id)}
                                                    >
                                                        <X size={14} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {errors.items && (
                        <div className="text-danger mt-2">
                            {errors.items}
                        </div>
                    )}
                </div>

                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="transport_number">Transport raqami</label>
                            <input
                                type="text"
                                name="transport_number"
                                className={`form-control ${errors.transport_number ? 'is-invalid' : ''}`}
                                value={inputs?.transport_number || ''}
                                onChange={handleChange}
                                placeholder="Transport raqamini kiriting"
                            />
                            {errors.transport_number && <div className="invalid-feedback">{errors.transport_number}</div>}
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <label htmlFor="driver_name">Haydovchi</label>
                            <input
                                type="text"
                                name="driver_name"
                                className={`form-control ${errors.driver_name ? 'is-invalid' : ''}`}
                                value={inputs?.driver_name || ''}
                                onChange={handleChange}
                                placeholder="Haydovchi ismini kiriting"
                            />
                            {errors.driver_name && <div className="invalid-feedback">{errors.driver_name}</div>}
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <label htmlFor="description">Izoh</label>
                    <textarea
                        name="description"
                        className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                        value={inputs?.description || ''}
                        placeholder="Sotuv uchun izoh"
                        onChange={handleChange}
                        rows="3"
                    />
                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                </div>

                <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={handleCloseForm}>
                        Bekor qilish
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Saqlanmoqda...' : 'Saqlash'}
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default SparepartSaleCreateForm;
